import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";

interface DashboardStats {
  customers: number;
  licencesActive: number;
  licencesLapsed: number;
  licencesRevoked: number;
  seatsPurchased: number;
  seatsActive: number;
  devicesOffline60: number;
  overAllocated: number;
  expiring30: number;
  newDevices7: number;
  newDevices30: number;
  downloadsTotal: number;
  downloads30: number;
  activeTrials: number;
  newTrials7: number;
  annualRunRatePence: number;
}

interface OverAllocatedRow {
  licence_id: string;
  customer_name: string;
  customer_email: string;
  seats_purchased: number;
  seats_active: number;
  expiry_date: string;
}

interface Attention {
  devicesBelowMinimum: number;
  minAppVersion: string | null;
  silentLicensed: { machineId: string; shortId: string; deviceName: string | null; lastSeenUtc: string; customerName: string }[];
}

function gbp(pence: number): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(
    pence / 100
  );
}

function StatCard({ label, value, tone }: { label: string; value: string | number; tone?: "warn" | "danger" }) {
  return (
    <div className={`stat-card ${tone ? `stat-${tone}` : ""}`}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [overAllocated, setOverAllocated] = useState<OverAllocatedRow[]>([]);
  const [attention, setAttention] = useState<Attention | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ stats: DashboardStats; overAllocated: OverAllocatedRow[]; attention?: Attention }>("/admin/dashboard")
      .then((res) => {
        setStats(res.stats);
        setOverAllocated(res.overAllocated);
        setAttention(res.attention ?? null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load dashboard"));
  }, []);

  if (error) return <p className="error-text">{error}</p>;
  if (!stats) return <p>Loading…</p>;

  const seatUsePct = stats.seatsPurchased > 0 ? Math.round((stats.seatsActive / stats.seatsPurchased) * 100) : 0;

  return (
    <div>
      <h1>Dashboard</h1>

      <div className="stat-grid">
        <StatCard label="Customers" value={stats.customers} />
        <StatCard label={`Downloads (${stats.downloads30} in 30d)`} value={stats.downloadsTotal} />
        <StatCard label={`Active trials (${stats.newTrials7} new this week)`} value={stats.activeTrials} />
        <StatCard label={`Active seats (${seatUsePct}% of ${stats.seatsPurchased})`} value={stats.seatsActive} />
        <StatCard label="Annual run-rate" value={gbp(stats.annualRunRatePence)} />
        <StatCard label="Active licences" value={stats.licencesActive} />
        <StatCard
          label="Over-allocated customers"
          value={stats.overAllocated}
          tone={stats.overAllocated > 0 ? "danger" : undefined}
        />
        <StatCard
          label="Expiring in 30 days"
          value={stats.expiring30}
          tone={stats.expiring30 > 0 ? "warn" : undefined}
        />
        <StatCard label="New devices (30d)" value={stats.newDevices30} />
        <StatCard label="New devices (7d)" value={stats.newDevices7} />
        <StatCard
          label="Devices offline > 60 days"
          value={stats.devicesOffline60}
          tone={stats.devicesOffline60 > 0 ? "warn" : undefined}
        />
        <StatCard label="Lapsed licences" value={stats.licencesLapsed} tone={stats.licencesLapsed > 0 ? "warn" : undefined} />
        <StatCard label="Revoked / suspended" value={stats.licencesRevoked} />
      </div>

      {attention && (attention.devicesBelowMinimum > 0 || attention.silentLicensed.length > 0) && (
        <>
          <h2 style={{ marginTop: 28 }}>Needs attention</h2>
          <ul className="attention-list">
            {attention.devicesBelowMinimum > 0 && (
              <li>
                <strong>{attention.devicesBelowMinimum}</strong> device{attention.devicesBelowMinimum === 1 ? " is" : "s are"} below the
                minimum app version{attention.minAppVersion ? ` ()` : ""} — <Link to="/devices">see Devices</Link>.
              </li>
            )}
            {attention.silentLicensed.length > 0 && (
              <li>
                <strong>{attention.silentLicensed.length}</strong> licensed device{attention.silentLicensed.length === 1 ? " hasn't" : "s haven't"} checked
                in for 3+ days (they lock after 48 hours without the licence service):
                <ul>
                  {attention.silentLicensed.map((d) => (
                    <li key={d.machineId}>
                      {d.deviceName ?? <code>{d.shortId}</code>} · {d.customerName} · last seen{" "}
                      {new Date(d.lastSeenUtc).toLocaleDateString(undefined, { dateStyle: "medium" })}
                    </li>
                  ))}
                </ul>
              </li>
            )}
          </ul>
        </>
      )}

      <h2 style={{ marginTop: 28 }}>Over-allocated customers</h2>
      {overAllocated.length === 0 ? (
        <p className="muted-subtitle">No customers are over their purchased seats.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Licence</th>
              <th>Active / purchased</th>
              <th>Over by</th>
              <th>Expiry</th>
            </tr>
          </thead>
          <tbody>
            {overAllocated.map((r) => (
              <tr key={r.licence_id}>
                <td>
                  <Link to="/customers">{r.customer_name}</Link>
                  <div className="muted-subtitle">{r.customer_email}</div>
                </td>
                <td>{r.licence_id}</td>
                <td className="text-danger">
                  <strong>{r.seats_active}</strong> / {r.seats_purchased}
                </td>
                <td className="text-danger">+{r.seats_active - r.seats_purchased}</td>
                <td>{r.expiry_date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
